import React from 'react';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';

import { rowsPerPageOptions } from '../../config';
import './styles.scss';
import { ColumnDef } from '@tanstack/react-table';
import { IconComponent, icons } from '../../componets/icons';
import { IFormInputPropsFilter } from '../../componets/interfaces/formInputPropsFilter';
import { IFormSettinsProps } from '../../componets/interfaces/formProps';
import ListComponet from '../../componets/list';
import { ImperativeHandleAccessedByParentListComponent } from '../../componets/interfaces/listProps';
import {
    formatDate,
    momentZoneToDateAddDays,
    momentZoneToUnix,
} from '../../util/dateUtil';
import * as ImplEnum from '../../communs/enums/generic-enum';
import { setHours, setMinutes } from 'date-fns';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { useAuth } from '../../context/AuthContext';
import { setTypeUserInQuery } from '../../util/configTypeUser';

const enumInstances = {
    expertiseSituationOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.expertiseSituationOptions,
    ),
};

const defaultValues = {
    user: [],
    dateInitial: momentZoneToDateAddDays({ days: -90, startOfType: 'day' }),
    dateFinal: momentZoneToDateAddDays({ days: 10, endOfType: 'day' }),
};

const LegalProcessDistrictList: React.FC<IFormSettinsProps> = props => {
    const { settings } = props;
    const { user } = useAuth();

    const refForm =
        React.useRef<ImperativeHandleAccessedByParentListComponent>(null);

    const columnsTable = React.useMemo<ColumnDef<object>[]>(
        () => [
            {
                id: 'status',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Status
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {
                                enumInstances.expertiseSituationOptionsInstance.getObject(
                                    row.original.status,
                                )?.description
                            }
                        </TableCell>
                    );
                },
            },
            {
                id: 'legalprocess',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Número processo
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.legalProcess?.processNumber}
                        </TableCell>
                    );
                },
            },
            {
                id: 'legalprocess',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Cliente
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.legalProcess?.customer?.name}
                        </TableCell>
                    );
                },
            },
            {
                id: 'details',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Detalhes
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original.details}
                        </TableCell>
                    );
                },
            },
            {
                id: 'local',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Local
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original.local}
                        </TableCell>
                    );
                },
            },
            {
                id: 'user',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Advogado
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.user?.name}
                        </TableCell>
                    );
                },
            },
            {
                accessorKey: 'dateAudience',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Data e hora
                    </TableCell>
                ),
                cell: (cell: any) => (
                    <TableCell
                        align="left"
                        onClick={() =>
                            refForm?.current?.handleClick(cell.row.original.id)
                        }>
                        {cell?.row.original.dateAudience
                            ? formatDate(cell?.row.original?.dateAudience)
                            : ''}
                    </TableCell>
                ),
            },
            {
                id: 'diffInDays',
                header: () => (
                    <TableCell align={'left'} padding={'normal'}>
                        Dias faltantes
                    </TableCell>
                ),
                cell: ({ row }: any) => {
                    return (
                        <TableCell
                            align="left"
                            key={row.id}
                            onClick={() =>
                                refForm?.current?.handleClick(row.original.id)
                            }>
                            {row.original?.diffInDays}
                        </TableCell>
                    );
                },
            },
            {
                id: 'select',
                header: ({ table }: any) => <TableCell align="center" />,
                cell: ({ row }: any) => (
                    <TableCell align="right">
                        <IconButton
                            onClick={() =>
                                refForm.current?.handleEdit(row.original.id)
                            }>
                            <IconComponent icon={icons.ModeEdit} />
                        </IconButton>
                        <IconButton
                            onClick={() => {
                                refForm.current?.setIdDelete(row.original.id);
                                refForm.current?.setOpenModalDelete(true);
                            }}>
                            <IconComponent icon={icons.Delete} />
                        </IconButton>
                    </TableCell>
                ),
            },
        ],
        [],
    );

    const QUERY_DEFAULT = `page=1&limit=${
        rowsPerPageOptions[0]
    }&dateInitial=${momentZoneToUnix(
        defaultValues.dateInitial,
    )}&dateFinal=${momentZoneToUnix(defaultValues.dateFinal)}&user.id.array=${
        user.sub
    }&audience.status=01`;

    const inputsFilter = React.useMemo<IFormInputPropsFilter[]>(
        () => [
            {
                typeInput: 'text',
                name: 'legalProcess.processNumber.ilike',
                control: null,
                label: 'Número do process',
                setValue: null,
                size: 'small',
                variant: 'standard',
                handleOnKeyPress: (data: any, name?: string) => {
                    if (data.charCode === 13 && name) {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            data.target.value,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'audience.status',
                variant: 'standard',
                control: null,
                label: 'Status',
                loadingAutocomplete: false,
                setValue: null,
                options:
                    enumInstances.expertiseSituationOptionsInstance.optionsList(),
                activeDebounce: false,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
            },
            {
                typeInput: 'autocomplete',
                name: 'customer.id',
                variant: 'standard',
                control: null,
                label: 'Cliente',
                loadingAutocomplete: false,
                setValue: null,
                handleChange: (event: any, option: any, name: string) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            option?.id,
                            name,
                        );
                    }
                },
                activeDebounce: true,
                query: (value: string) => `customer?name=${value}`,
            },
            {
                typeInput: 'multiple',
                name: 'user.id.array',
                variant: 'standard',
                control: null,
                label: 'Advogado',
                handleChange: (event: any, option: any[]) => {
                    if (event.type === 'click' || event.type === 'keydown') {
                        const data = option?.map(o => o.id);
                        refForm?.current?.handleByOnKeyPressEnterNewQuery(
                            data.length > 0 ? data : null,
                            'user.id.array',
                        );
                    }
                },
                setValue: null,
                entityPath: 'users',
                keyQuery: 'user.id',
                activeDebounce: true,
                query: (value: string) =>
                    `users?${setTypeUserInQuery(user.typeUser)}&limit=${value}`,
                DAODataList: (value: any) => {
                    return {
                        id: value.id,
                        description: value.name,
                    };
                },
            },
        ],
        [],
    );

    const mainInputsFilter = React.useMemo<FormInputProps[]>(
        () => [
            {
                typeInput: 'dateHour',
                name: 'dateInitial',
                control: null,
                label: 'Date de início',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 0);
                    newTime = setMinutes(newTime, 1);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateInitial',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
            {
                typeInput: 'dateHour',
                name: 'dateFinal',
                control: null,
                label: 'Date de fim',
                loadingAutocomplete: false,
                md: 6,
                xs: 12,
                handleChange: (data: any) => {
                    let newTime = setHours(data, 23);
                    newTime = setMinutes(newTime, 59);
                    refForm?.current?.handleByOnKeyPressEnterNewQuery(
                        momentZoneToUnix(newTime),
                        'dateFinal',
                    );
                },
                setValue: null,
                fullWidth: true,
                clearErrors: null,
                setError: null,
            },
        ],
        [],
    );

    return (
        <ListComponet
            settings={settings}
            defaultValues={defaultValues}
            columnsTable={columnsTable}
            inputsFilter={inputsFilter}
            mainInputsFilter={mainInputsFilter}
            QUERY_DEFAULT={QUERY_DEFAULT}
            ref={refForm}
        />
    );
};

export default LegalProcessDistrictList;
